import React, { useState } from 'react';
import {
  Drawer, List, ListItem, ListItemButton, ListItemText,
  Box, Typography, TextField, InputAdornment, Collapse, Stack, Tooltip, Switch, FormControlLabel
} from '@mui/material';
import { useEffect } from 'react';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { tableLabel } from '../config/tableLabels';
import { sidebarTooltips } from '../config/sidebarTooltips';
import api from '../services/api';
import SearchIcon       from '@mui/icons-material/Search';
import TableChartIcon   from '@mui/icons-material/TableChart';
import BusinessIcon     from '@mui/icons-material/Business';
import DateRangeIcon    from '@mui/icons-material/DateRange';
import LayersIcon       from '@mui/icons-material/Layers';
import AttachMoneyIcon  from '@mui/icons-material/AttachMoney';
import PeopleIcon       from '@mui/icons-material/People';
import GavelIcon        from '@mui/icons-material/Gavel';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import TrendingUpIcon   from '@mui/icons-material/TrendingUp';
import CreditCardIcon   from '@mui/icons-material/CreditCard';
import AccountTreeIcon  from '@mui/icons-material/AccountTree';
import ShieldIcon       from '@mui/icons-material/Shield';
import EventNoteIcon    from '@mui/icons-material/EventNote';
import StarIcon         from '@mui/icons-material/Star';
import MenuBookIcon     from '@mui/icons-material/MenuBook';
import ExpandMoreIcon   from '@mui/icons-material/ExpandMore';
import ExpandLessIcon   from '@mui/icons-material/ExpandLess';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import DescriptionIcon  from '@mui/icons-material/Description';
import BuildIcon        from '@mui/icons-material/Build';

const DRAWER_WIDTH = 260;

// Custom page groups (not table-based) — keys prefixed with __page:
const CUSTOM_GROUPS = {
  'Salary Management': {
    icon: <AccountBalanceWalletIcon />,
    afterGroup: 'Element Setup',
    items: [
      { key: '__page:SALARY_TEMPLATE', label: 'Salary Templates', icon: <DescriptionIcon /> },
    ],
  },
};

const TABLE_GROUPS = {
  'Org Structure':    { tables: ['LEGISLATION_DATA_GROUP','DEPARTMENT','DESIGNATION','BUSINESS_TYPE','COMPANY','LOCATION','BUSINESS_GROUP','MY_MODULES'], icon: <BusinessIcon /> },
  'Pay Foundation':   { tables: ['CONSOLIDATION_GROUP','PAYROLL','PAY_PERIOD'], icon: <DateRangeIcon /> },
  'Payroll Run':      { tables: ['PAYROLL_RUN', 'FULL_FINAL_SETTLEMENT'], icon: <TrendingUpIcon /> },
  'Element Setup':    { tables: ['ELEMENT_TYPE'], icon: <LayersIcon /> },
  'Workforce':        { tables: ['PERSON','ASSIGNMENT','PAYROLL_RELATIONSHIP'], icon: <PeopleIcon /> },
  'Salary & Entries': { tables: ['SALARY','SALARY_REVISION_HISTORY','ELEMENT_ENTRY'], icon: <AttachMoneyIcon /> },
  'Tax':              { tables: ['TAX_DECLARATION','TAX_REGIME_CONFIG','FORM16_SUMMARY'], icon: <GavelIcon /> },
  'Advance Salary Structure': { tables: ['ADVANCE_SALARY_POLICY','ADVANCE_SALARY_REQUEST','ADVANCE_SALARY','ADVANCE_SALARY_REPAYMENT'], icon: <AccountBalanceIcon /> },
  'Payments':         { tables: ['PERSONAL_PAYMENT_METHOD','PAYMENT','PAYSLIP','BANK_PAYMENT_FILE'], icon: <CreditCardIcon /> },
  'Statutory':        { tables: ['PF_ACCOUNT','ESI_ACCOUNT'], icon: <ShieldIcon /> },
  'Masters':          { tables: ['BANK_MASTER','TAX_AUTHORITY'], icon: <StarIcon /> },
  'Lookup':           { tables: [
                          'BALANCE_CATEGORY',
                          'CURRENCY',
                          'PERIOD_TYPE',
                          'PAY_PERIOD_STATUS',
                          'ASSIGNMENT_STATUS_MASTER',
                          'EMPLOYMENT_TYPE_MASTER',
                          'RELATIONSHIP_TYPE_MASTER',
                          'GRADE_MASTER',
                          'PROOF_TYPE_MASTER',
                          'TAX_DECLARATION_STATUS_MASTER',
                          'FORM16_GENERATION_STATUS_MASTER',
                          'ADVANCE_SALARY_TYPE',
                        ], icon: <MenuBookIcon /> },
  'Diagnostics': {
    icon: <BuildIcon />, diagnostic: true,
    subgroups: {
      'Scheduling':       { tables: ['WORK_SCHEDULE','SHIFT_DEFINITION','HOLIDAY_CALENDAR','ABSENCE_TYPE'], icon: <EventNoteIcon /> },
      'Workforce':        { tables: ['ATTENDANCE','LEAVE_BALANCE'],                                        icon: <PeopleIcon /> },
      'Element Setup':    { tables: ['ELEMENT_INPUT_VALUE','ELEMENT_ELIGIBILITY','PAYROLL_FORMULA'],       icon: <LayersIcon /> },
      'Salary & Entries': { tables: ['ELEMENT_ENTRY_VALUE'],                                               icon: <AttachMoneyIcon /> },
      'Payroll Run':      { tables: ['RUN_RESULT' /*,'RUN_RESULT_VALUE'*/],                                     icon: <TrendingUpIcon /> },
      'Balances':         { tables: ['BALANCE_DEFINITION','DEFINED_BALANCE','BALANCE_VALUE'],              icon: <AccountTreeIcon /> },
      'Costing & GL':     { tables: ['PAYROLL_COSTING','GL_ACCOUNT_MASTER'],                               icon: <AccountTreeIcon /> },
    },
  },
};

export default function Sidebar({ tables, activeTable, activePage, onSelect, activeContext, onExitContext }) {
  const [search,    setSearch]    = useState('');
  const [collapsed, setCollapsed] = useState({ Diagnostics: true });
  const [unreadCounts, setUnreadCounts] = useState({});
  
  useEffect(() => {
    const fetchCounts = () => {
      api.get('/notifications/unread-counts').then(res => {
        if (res.data?.success) setUnreadCounts(res.data.data);
      }).catch(e => console.error(e));
    };
    fetchCounts();
    const interval = setInterval(fetchCounts, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleSelect = (tableName) => {
    if (unreadCounts[tableName] > 0) {
      setUnreadCounts(prev => ({ ...prev, [tableName]: 0 }));
      api.put(`/notifications/mark-read/${tableName}`).catch(e => console.error(e));
    }
    onSelect(tableName);
  };
  
  const [showTooltips, setShowTooltips] = useState(() => {
    const saved = localStorage.getItem('sidebar_show_tooltips');
    return saved !== null ? saved === 'true' : true;
  });

  useEffect(() => {
    localStorage.setItem('sidebar_show_tooltips', showTooltips);
  }, [showTooltips]);

  const filteredTables = tables.filter(t =>
    t.name.toLowerCase().includes(search.toLowerCase())
  );

  const getCount  = (name) => { const t = tables.find(x => x.name === name); return t ? t.count : null; };
  const toggleGroup = (g) => setCollapsed(prev => ({ ...prev, [g]: !prev[g] }));

  // When no active payroll context, only the Org Structure group is visible.
  // User must configure a Company + click "Manage Payroll" to unlock the rest.
  const visibleGroups = activeContext
    ? Object.entries(TABLE_GROUPS)
    : Object.entries(TABLE_GROUPS).filter(([g]) => g === 'Org Structure');

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: DRAWER_WIDTH, flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: DRAWER_WIDTH, boxSizing: 'border-box',
          bgcolor: 'white', color: '#111827',
          borderRight: '1.5px solid #E5E7EB',
          boxShadow: 'none',
        },
      }}
    >
      {/* ── Logo ── */}
      <Box sx={{
        px: 2.5, py: 2,
        borderBottom: '1.5px solid #E5E7EB',
        display: 'flex', alignItems: 'center', gap: 1.5,
      }}>
        <Box sx={{
          width: 36, height: 36, borderRadius: '10px',
          background: 'linear-gradient(135deg,#16a34a,#22c55e)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 4px 10px rgba(34,197,94,0.35)',
          flexShrink: 0,
        }}>
          <Typography fontWeight={900} fontSize={16} color="white">V</Typography>
        </Box>
        <Box>
          <Typography fontWeight={800} fontSize={14} color="#111827" lineHeight={1.2}>VVSPL HRMS</Typography>
          <Typography fontSize={10.5} color="#9CA3AF" letterSpacing={0.3}>Payroll Management</Typography>
        </Box>
      </Box>

      {/* ── Active payroll context banner ── */}
      {activeContext ? (
        <Box sx={{ mx: 1.5, mt: 1.2, p: 1.2, borderRadius: '10px',
          background: 'linear-gradient(135deg,#f0fdf4,#dcfce7)',
          border: '1.5px solid #bbf7d0' }}>
          <Stack direction="row" alignItems="center" gap={1}>
            <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#16a34a', flexShrink: 0 }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography fontSize={10.5} fontWeight={700} color="#16a34a"
                sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Active Context
              </Typography>
              <Typography fontSize={12.5} fontWeight={700} color="#111827" noWrap title={activeContext.company_name}>
                {activeContext.company_name}
              </Typography>
              <Typography fontSize={10.5} color="#6B7280" noWrap>
                {activeContext.bg_name} · {activeContext.ldg_name}
              </Typography>
            </Box>
            <Tooltip title="Exit payroll context" arrow>
              <Box onClick={onExitContext}
                sx={{ cursor: 'pointer', p: 0.4, borderRadius: '6px',
                  color: '#DC2626', '&:hover': { bgcolor: '#FEF2F2' } }}>
                <CloseIcon sx={{ fontSize: 14 }} />
              </Box>
            </Tooltip>
          </Stack>
        </Box>
      ) : (
        <Box sx={{ mx: 1.5, mt: 1.2, p: 1.2, borderRadius: '10px',
          bgcolor: '#FEF3C7', border: '1.5px solid #FDE68A' }}>
          <Typography fontSize={10.5} fontWeight={700} color="#92400E"
            sx={{ textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.3 }}>
            Setup Required
          </Typography>
          <Typography fontSize={11} color="#78350F" lineHeight={1.4}>
            Configure a Company and click "Manage Payroll" to unlock the full app.
          </Typography>
        </Box>
      )}

      {/* ── Search ── */}
      <Box sx={{ px: 1.5, pt: 1.5, pb: 0.5 }}>
        <TextField
          size="small" placeholder="Search tables…"
          value={search} onChange={e => setSearch(e.target.value)}
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#9CA3AF', fontSize: 16 }} />
              </InputAdornment>
            ),
            sx: {
              bgcolor: '#F3F4F6', borderRadius: '8px', fontSize: 12.5, color: '#374151',
              '& fieldset': { border: '1.5px solid #E5E7EB' },
              '&:hover fieldset': { borderColor: '#22c55e !important' },
              '&.Mui-focused fieldset': { borderColor: '#22c55e !important' },
            },
          }}
        />
      </Box>

      {/* ── Nav list ── */}
      <Box sx={{
        overflow: 'auto', flex: 1, pb: 2, pt: 0.5,
        '&::-webkit-scrollbar': { width: 3 },
        '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
        '&::-webkit-scrollbar-thumb': { bgcolor: '#E5E7EB', borderRadius: 2 },
      }}>

        {search ? (
          /* ── Search results ── */
          <List dense sx={{ px: 1, pt: 0.5 }}>
            {filteredTables.map(t => {
              const isActive = activeTable === t.name;
              return (
                <ListItem key={t.name} disablePadding sx={{ mb: 0.3 }}>
                  <ListItemButton
                    selected={isActive}
                    onClick={() => handleSelect(t.name)}
                    sx={{
                      borderRadius: '8px', py: 0.7, px: 1.5,
                      ...(isActive
                        ? { bgcolor: '#22c55e !important', '&:hover': { bgcolor: '#16a34a !important' } }
                        : { '&:hover': { bgcolor: '#F3F4F6' } }),
                    }}
                  >
                    <TableChartIcon sx={{ fontSize: 13, mr: 1, color: isActive ? 'white' : '#22c55e' }} />
                    <ListItemText
                      primary={tableLabel(t.name)}
                      primaryTypographyProps={{ fontSize: 13, fontWeight: isActive ? 600 : 500 }}
                      sx={{ margin: 0, opacity: isActive ? 1 : 0.85 }}
                    />
                    {unreadCounts[t.name] > 0 && (
                      <Box sx={{
                        width: 8, height: 8, borderRadius: '50%', bgcolor: '#ef4444', ml: 1,
                        animation: 'pulse 1.5s infinite',
                        '@keyframes pulse': {
                          '0%': { transform: 'scale(0.95)', boxShadow: '0 0 0 0 rgba(239, 68, 68, 0.7)' },
                          '70%': { transform: 'scale(1)', boxShadow: '0 0 0 6px rgba(239, 68, 68, 0)' },
                          '100%': { transform: 'scale(0.95)', boxShadow: '0 0 0 0 rgba(239, 68, 68, 0)' }
                        }
                      }} />
                    )}
                    {t.count > 0 && (
                      <Box sx={{
                        px: 0.8, py: 0.1, borderRadius: '5px', minWidth: 20, textAlign: 'center',
                        bgcolor: isActive ? 'rgba(255,255,255,0.25)' : '#F3F4F6',
                      }}>
                        <Typography fontSize={10} fontWeight={700} color={isActive ? 'white' : '#6B7280'}>
                          {t.count}
                        </Typography>
                      </Box>
                    )}
                  </ListItemButton>
                </ListItem>
              );
            })}
          </List>
        ) : (
          /* ── Grouped nav ── */
          (() => {
            const tableGroupEntries = visibleGroups;
            const result = [];

            // Helper — render a single table list item (reused for top-level + nested)
            const renderTableItem = (tn) => {
              const count    = getCount(tn);
              const isActive = activeTable === tn && !activePage;
              const tooltipText = showTooltips ? sidebarTooltips[tn] : '';

              return (
                <ListItem key={tn} disablePadding sx={{ mb: 0.15 }}>
                  <Tooltip title={tooltipText || ''} placement="right" arrow disableInteractive>
                    <ListItemButton selected={isActive} onClick={() => handleSelect(tn)}
                      sx={{ borderRadius: '8px', py: 0.75, pl: 2.5, pr: 1.5, transition: 'background 0.12s',
                        ...(isActive ? { bgcolor: '#22c55e !important', '&:hover': { bgcolor: '#16a34a !important' } }
                          : { '&:hover': { bgcolor: '#F3F4F6' } }) }}>
                      <Box sx={{ width: 7, height: 7, borderRadius: '50%', mr: 1.5, flexShrink: 0,
                        bgcolor: isActive ? 'white' : '#D1D5DB', transition: 'background 0.12s' }} />
                      <ListItemText primary={tableLabel(tn)}
                        primaryTypographyProps={{ fontSize: 13, fontWeight: isActive ? 600 : 500 }}
                        sx={{ margin: 0, opacity: isActive ? 1 : 0.85 }}
                      />
                      {unreadCounts[tn] > 0 && (
                        <Box sx={{
                          width: 8, height: 8, borderRadius: '50%', bgcolor: '#ef4444', ml: 1, flexShrink: 0,
                          animation: 'pulse 1.5s infinite',
                          '@keyframes pulse': {
                            '0%': { transform: 'scale(0.95)', boxShadow: '0 0 0 0 rgba(239, 68, 68, 0.7)' },
                            '70%': { transform: 'scale(1)', boxShadow: '0 0 0 6px rgba(239, 68, 68, 0)' },
                            '100%': { transform: 'scale(0.95)', boxShadow: '0 0 0 0 rgba(239, 68, 68, 0)' }
                          }
                        }} />
                      )}
                      {showTooltips && tooltipText && (
                        <InfoOutlinedIcon sx={{ fontSize: 14, color: isActive ? 'rgba(255,255,255,0.7)' : '#9CA3AF', mr: 1, ml: 0.5 }} />
                      )}
                      {count !== null && count > 0 && (
                        <Box sx={{ px: 0.8, py: 0.1, borderRadius: '5px', minWidth: 20, textAlign: 'center',
                          bgcolor: isActive ? 'rgba(255,255,255,0.22)' : '#F3F4F6' }}>
                          <Typography fontSize={10} fontWeight={500} color={isActive ? 'white' : '#6B7280'}>{count}</Typography>
                        </Box>
                      )}
                    </ListItemButton>
                  </Tooltip>
                </ListItem>
              );
            };

            tableGroupEntries.forEach(([group, groupDef], gi) => {
              const { tables: tableList, icon, diagnostic, subgroups } = groupDef;

              // Compute available tables (flat or from subgroups) for the empty-skip check
              const allTablesFlat = subgroups
                ? Object.values(subgroups).flatMap(s => s.tables)
                : (tableList || []);
              const available = allTablesFlat.filter(tn => tables.find(t => t.name === tn));
              if (!available.length) return;
              const isCollapsed = collapsed[group];

              result.push(
                <Box key={group} sx={{ mb: 0.5, ...(diagnostic && { mt: 1.2, pt: 0.8, borderTop: '1px dashed #E5E7EB' }) }}>
                  <Tooltip title={showTooltips && group === 'Diagnostics' ? sidebarTooltips.__group_Diagnostics || '' : ''} placement="right" arrow disableInteractive>
                    <Box onClick={() => toggleGroup(group)}
                      sx={{ display: 'flex', alignItems: 'center', gap: 1.5,
                        mx: 1, px: 1.5, py: 0.9, cursor: 'pointer', borderRadius: '8px',
                        transition: 'background 0.12s', '&:hover': { bgcolor: '#F3F4F6' } }}>
                      <Box sx={{ color: '#6B7280', display: 'flex', flexShrink: 0 }}>
                        {React.cloneElement(icon, { sx: { fontSize: diagnostic ? 16 : 18, color: diagnostic ? '#94A3B8' : '#6B7280' } })}
                      </Box>
                      <Typography sx={{ flex: 1, fontSize: diagnostic ? 12 : 13.5, fontWeight: diagnostic ? 600 : 500,
                        color: diagnostic ? '#94A3B8' : '#374151',
                        ...(diagnostic && { textTransform: 'uppercase', letterSpacing: 0.6 }) }}>
                        {group}
                      </Typography>
                      {diagnostic && (
                        <Box sx={{ px: 0.7, py: 0.05, borderRadius: '4px', bgcolor: '#F1F5F9',
                          border: '1px solid #E5E7EB', mr: 0.4 }}>
                          <Typography fontSize={8.5} fontWeight={700} color="#94A3B8" sx={{ letterSpacing: 0.5 }}>
                            INTERNAL
                          </Typography>
                        </Box>
                      )}
                      {isCollapsed ? <ExpandMoreIcon sx={{ fontSize: 16, color: '#9CA3AF' }} /> : <ExpandLessIcon sx={{ fontSize: 16, color: '#9CA3AF' }} />}
                    </Box>
                  </Tooltip>
                  <Collapse in={!isCollapsed}>
                    {subgroups ? (
                      // Nested rendering — each subgroup is its own mini-collapsible
                      <Box sx={{ pb: 0.5 }}>
                        {Object.entries(subgroups).map(([subName, subDef]) => {
                          const subAvailable = subDef.tables.filter(tn => tables.find(t => t.name === tn));
                          if (!subAvailable.length) return null;
                          const subKey = `${group}__${subName}`;
                          const isSubCollapsed = collapsed[subKey] ?? true; // default collapsed
                          return (
                            <Box key={subKey} sx={{ mx: 1, mb: 0.3 }}>
                              <Box onClick={() => toggleGroup(subKey)}
                                sx={{ display: 'flex', alignItems: 'center', gap: 1,
                                  px: 1.2, py: 0.6, cursor: 'pointer', borderRadius: '6px',
                                  bgcolor: '#FAFBFC',
                                  '&:hover': { bgcolor: '#F1F5F9' } }}>
                                {subDef.icon && React.cloneElement(subDef.icon, { sx: { fontSize: 13, color: '#94A3B8' } })}
                                <Typography sx={{ flex: 1, fontSize: 11.5, fontWeight: 600, color: '#64748B',
                                  letterSpacing: 0.3 }}>
                                  {subName}
                                </Typography>
                                <Typography fontSize={9.5} color="#94A3B8" sx={{ fontWeight: 600 }}>
                                  {subAvailable.length}
                                </Typography>
                                {isSubCollapsed
                                  ? <ExpandMoreIcon sx={{ fontSize: 13, color: '#9CA3AF' }} />
                                  : <ExpandLessIcon sx={{ fontSize: 13, color: '#9CA3AF' }} />}
                              </Box>
                              <Collapse in={!isSubCollapsed}>
                                <List dense disablePadding sx={{ pl: 1, pt: 0.3 }}>
                                  {subAvailable.map(renderTableItem)}
                                </List>
                              </Collapse>
                            </Box>
                          );
                        })}
                      </Box>
                    ) : (
                      <List dense disablePadding sx={{ px: 1, pb: 0.5 }}>
                        {available.map(renderTableItem)}
                      </List>
                    )}
                  </Collapse>
                </Box>
              );

              // Inject custom groups that belong after this table group
              Object.entries(CUSTOM_GROUPS).forEach(([cGroup, cDef]) => {
                if (cDef.afterGroup !== group) return;
                const isCCollapsed = collapsed[cGroup];
                result.push(
                  <Box key={cGroup} sx={{ mb: 0.5 }}>
                    <Box onClick={() => toggleGroup(cGroup)}
                      sx={{ display: 'flex', alignItems: 'center', gap: 1.5,
                        mx: 1, px: 1.5, py: 0.9, cursor: 'pointer', borderRadius: '8px',
                        transition: 'background 0.12s', '&:hover': { bgcolor: '#F3F4F6' } }}>
                      <Box sx={{ display: 'flex', flexShrink: 0 }}>
                        {React.cloneElement(cDef.icon, { sx: { fontSize: 18, color: '#6B7280' } })}
                      </Box>
                      <Typography sx={{ flex: 1, fontSize: 13.5, fontWeight: 500, color: '#374151' }}>{cGroup}</Typography>
                      {isCCollapsed ? <ExpandMoreIcon sx={{ fontSize: 16, color: '#9CA3AF' }} /> : <ExpandLessIcon sx={{ fontSize: 16, color: '#9CA3AF' }} />}
                    </Box>
                    <Collapse in={!isCCollapsed}>
                      <List dense disablePadding sx={{ px: 1, pb: 0.5 }}>
                        {cDef.items.map(item => {
                          const isActive = activePage === item.key.replace('__page:', '');
                          return (
                            <ListItem key={item.key} disablePadding sx={{ mb: 0.15 }}>
                              <Tooltip title={showTooltips ? sidebarTooltips[item.key] || '' : ''} placement="right" arrow disableInteractive>
                                <ListItemButton selected={isActive} onClick={() => handleSelect(item.key)}
                                  sx={{ borderRadius: '8px', py: 0.75, pl: 2.5, pr: 1.5,
                                    ...(isActive ? { bgcolor: '#22c55e !important', '&:hover': { bgcolor: '#16a34a !important' } }
                                      : { '&:hover': { bgcolor: '#F3F4F6' } }) }}>
                                  <Box sx={{ width: 7, height: 7, borderRadius: '50%', mr: 1.5, flexShrink: 0,
                                    bgcolor: isActive ? 'white' : '#D1D5DB' }} />
                                  <ListItemText primary={item.label}
                                    primaryTypographyProps={{ fontSize: 13, fontWeight: isActive ? 600 : 500 }}
                                    sx={{ margin: 0, opacity: isActive ? 1 : 0.85 }}
                                  />
                                  {unreadCounts[item.key] > 0 && (
                                    <Box sx={{
                                      width: 8, height: 8, borderRadius: '50%', bgcolor: '#ef4444', ml: 1, flexShrink: 0,
                                      animation: 'pulse 1.5s infinite',
                                      '@keyframes pulse': {
                                        '0%': { transform: 'scale(0.95)', boxShadow: '0 0 0 0 rgba(239, 68, 68, 0.7)' },
                                        '70%': { transform: 'scale(1)', boxShadow: '0 0 0 6px rgba(239, 68, 68, 0)' },
                                        '100%': { transform: 'scale(0.95)', boxShadow: '0 0 0 0 rgba(239, 68, 68, 0)' }
                                      }
                                    }} />
                                  )}
                                  {showTooltips && sidebarTooltips[item.key] && (
                                    <InfoOutlinedIcon sx={{ fontSize: 14, color: isActive ? 'rgba(255,255,255,0.7)' : '#9CA3AF', mr: 1, ml: 0.5 }} />
                                  )}
                                </ListItemButton>
                              </Tooltip>
                            </ListItem>
                          );
                        })}
                      </List>
                    </Collapse>
                  </Box>
                );
              });
            });

            return result;
          })()
        )}
      </Box>

      {/* ── Tooltip Toggle ── */}
      <Box sx={{ px: 2, pb: 1.5 }}>
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={showTooltips}
              onChange={(e) => setShowTooltips(e.target.checked)}
              color="success"
            />
          }
          label={<Typography fontSize={11} color="#6B7280" fontWeight={500}>Show Sidebar Tooltips</Typography>}
          sx={{ m: 0 }}
        />
      </Box>

      {/* ── Footer ── */}
      <Box sx={{
        mx: 1.5, mb: 1.5, p: 1.5, borderRadius: '10px',
        border: '1.5px solid #E5E7EB', bgcolor: '#F9FAFB',
        display: 'flex', alignItems: 'center', gap: 1.2,
      }}>
        <Box sx={{
          width: 32, height: 32, borderRadius: '50%',
          background: 'linear-gradient(135deg,#16a34a,#22c55e)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          <Typography fontSize={13} fontWeight={800} color="white">A</Typography>
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography fontSize={12} fontWeight={700} color="#111827" noWrap>Admin User</Typography>
          <Typography fontSize={10.5} color="#9CA3AF">
            {tables.length} tables · {tables.reduce((s, t) => s + t.count, 0)} records
          </Typography>
        </Box>
        <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#22c55e', flexShrink: 0 }} />
      </Box>
    </Drawer>
  );
}
